"use client";

import React, { useEffect, useState, useCallback } from 'react'; // Removed useRef, added useCallback
import { useDispatch, useSelector } from 'react-redux';
import { selectUnlockedCandidateList, UnlockedCandidateListAsync } from '../employerDashboardSlice/UnlockedCandidateList/UnlockedCandidateListSlice';
import {
    Box,
    Text,
    Skeleton,
    Tooltip,
    GridItem,
    Badge,
    useToast,
} from "@chakra-ui/react"; // Removed Flex and HStack
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUser,
    faPlus,
    faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import noData from "./nodata.png";
import { TimeDifference } from "../../common/TimeDifference";
import { CalendarDaysIcon, EnvelopeIcon, EyeIcon, PlusIcon } from "@heroicons/react/24/outline";
import { fetchEmployerCandidateUnlockListExportAsync } from '../employerDataExports/employerCandidateUnlockListExport/employerCandidateUnlockListExportSlice';
import { PhoneIcon } from 'lucide-react';

const UnlockedCandidate = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const candidates = useSelector(selectUnlockedCandidateList);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCandidates, setTotalCandidates] = useState(0);
    const candidatesPerPage = 15;

    // Excel Headers configuration
    const excelHeaders = [
        { header: "Name", key: "candidate_name", width: 20 },
        { header: "Email", key: "candidate_email", width: 25 },
        { header: "Phone", key: "candidate_phone", width: 15 },
        { header: "Gender", key: "candidate_gender", width: 10 },
        { header: "Date of Birth", key: "candidate_dob", width: 15 },
        { header: "Experience (Years)", key: "candidate_experience_year", width: 18 },
        { header: "Experience (Months)", key: "candidate_experience_month", width: 18 },
        { header: "Work Status", key: "candidate_work_status", width: 15 },
        { header: "Current City", key: "candidate_current_city", width: 15 },
        { header: "Job Title", key: "candidate_title", width: 20 },
        { header: "Preferred Cities", key: "preferred_cities", width: 25 },
    ];

    // FIX: Wrapped fetchCandidates in useCallback to memoize it
    const fetchCandidates = useCallback(() => {
        setIsLoading(true);
        setHasError(false);
        dispatch(UnlockedCandidateListAsync({ page: currentPage, per_page: candidatesPerPage }))
            .unwrap()
            .then((response) => {
                if (response) {
                    setTotalCandidates(response.total || 0);
                    setTotalPages(response.last_page || 1);
                }
                setIsLoading(false);
            })
            .catch(() => {
                setHasError(true);
                setIsLoading(false);
            });
    }, [dispatch, currentPage, candidatesPerPage]);

    // FIX: Added fetchCandidates to dependency array
    useEffect(() => {
        fetchCandidates();
    }, [fetchCandidates]);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prevPage) => prevPage + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prevPage) => prevPage - 1);
            window.scrollTo(0, 0);
        }
    };

    const handleViewCandidateProfile = (candidateUuid) => {
        const url = `/employer-dashboard/show-candidate/${candidateUuid}`;
        window.open(url, "_blank");
    };

    const handleExportToExcel = async () => {
        setIsExporting(true);
        try {
            const result = await dispatch(fetchEmployerCandidateUnlockListExportAsync()).unwrap();

            if (Array.isArray(result)) {
                const formattedData = result.map(candidate => ({
                    candidate_name: candidate.candidate_name || 'N/A',
                    candidate_email: candidate.candidate_email || 'N/A',
                    candidate_phone: candidate.candidate_phone || 'Not provided',
                    candidate_gender: candidate.candidate_gender || 'N/A',
                    candidate_dob: candidate.candidate_dob || 'N/A',
                    candidate_experience_year: candidate.candidate_experience_year || 0,
                    candidate_experience_month: candidate.candidate_experience_month || 0,
                    candidate_work_status: candidate.candidate_work_status || 'N/A',
                    candidate_current_city: candidate.candidate_current_city || 'N/A',
                    candidate_title: candidate.candidate_title || 'N/A',
                    preferred_cities: candidate.candidate_preferred_city
                        ? candidate.candidate_preferred_city.map(city => city.location.title).join(', ')
                        : 'N/A',
                }));

                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet('Unlocked Candidates');
                worksheet.columns = excelHeaders;

                const headerRow = worksheet.getRow(1);
                headerRow.font = { bold: true };
                headerRow.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFF00' }
                };
                headerRow.alignment = { horizontal: 'center' };
                headerRow.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };

                formattedData.forEach(candidate => {
                    worksheet.addRow(candidate);
                });

                worksheet.eachRow((row, rowNumber) => {
                    row.eachCell((cell) => {
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };
                        if (rowNumber > 1) {
                            cell.alignment = { horizontal: 'center' };
                        }
                    });
                });

                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const fileName = `unlocked-candidates-details-${new Date().toISOString().split('T')[0]}.xlsx`;
                saveAs(blob, fileName);

                toast({
                    title: "Export Successful",
                    description: "Candidate data has been exported to Excel successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                throw new Error("Unexpected response format. Expected an array.");
            }
        } catch (error) {
            toast({
                title: "Export Failed",
                description: error.message || "Failed to export candidate data.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsExporting(false);
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 px-7 gap-2 w-full">
                    {[...Array(6)].map((_, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-md border border-gray-100 p-3">
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <Skeleton height="48px" width="48px" borderRadius="lg" />
                                    <div className="flex-1 space-y-2">
                                        <Skeleton height="20px" width="70%" />
                                        <Skeleton height="16px" width="50%" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Skeleton height="16px" width="100%" />
                                    <Skeleton height="16px" width="80%" />
                                </div>
                                <Skeleton height="36px" width="100%" />
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (hasError) {
            return (
                <Box textAlign="center" py="10">
                    <Text fontSize="xl" color="red.500">
                        Failed to load unlocked candidates. Please try again later.
                    </Text>
                </Box>
            );
        }

        if (!candidates || candidates.data?.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center w-full">
                    <img
                        src={noData || "/placeholder.svg"}
                        className="object-contain h-[300px] w-[300px]"
                        alt="No candidates"
                    />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        No Unlocked Candidates Found
                    </h3>
                    <p className="text-gray-600 text-center max-w-md mb-4">
                        You haven't unlocked any candidate profiles yet. Unlock candidate profiles to view their complete details.
                    </p>
                    <Link to={`/employer-dashboard/search-candidate`}>
                        <button className="inline-flex items-center px-3 py-2 mb-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-200">
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Find Candidates
                        </button>
                    </Link>
                </div>
            );
        }

        return (
            <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 w-full px-7">
                    {candidates.data?.map((item) => (
                        <div
                            key={item.uuid}
                            className="bg-white rounded-xl border border-gray-100 transition-all duration-300 overflow-hidden"
                        >
                            <div className="p-3 pb-2">
                                <div className="flex items-start gap-3">
                                    <div className="bg-blue-50 p-3 rounded-lg flex-shrink-0">
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className="text-blue-500 text-lg"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 leading-tight capitalize">
                                            {item.name || "Unknown Candidate"}
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <Badge colorScheme="green" size="sm" px={2} py={1} borderRadius="md">
                                                Unlocked
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="px-3 pb-2">
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <h4 className="text-xs text-gray-500 font-medium uppercase tracking-wide">Contact Information</h4>

                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <EnvelopeIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                            <Tooltip label="Email Address" placement="top" bg="gray.100" color="black" fontSize="sm">
                                                <span className="truncate font-medium">{item.email || "Not provided"}</span>
                                            </Tooltip>
                                        </div>

                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <PhoneIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                            <Tooltip label="Phone Number" placement="top" bg="gray.100" color="black" fontSize="sm">
                                                <span className="truncate font-medium">{item.phone || "Not provided"}</span>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between py-2 bg-gray-50 rounded-lg px-3">
                                        <div className="flex items-center gap-2">
                                            <CalendarDaysIcon className="h-4 w-4 text-green-500" />
                                            <span className="text-sm text-gray-600">Unlocked:</span>
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">
                                            <TimeDifference dateTimeString={item.unlocked_at} />
                                        </span>
                                    </div>

                                    <div className="border-t border-gray-100 my-3"></div>

                                    <button
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 font-medium"
                                        onClick={() => handleViewCandidateProfile(item.uuid)}
                                    >
                                        <EyeIcon className="h-4 w-4" />
                                        View Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pagination */}
                {candidates && candidates.data?.length > 0 && (
                    <div className="my-3 w-full px-7">
                        {/* FIX: Used totalCandidates to display total count */}
                        <Text textAlign="center" color="gray.600" mb={4}>
                            Total Unlocked Candidates: {totalCandidates}
                        </Text>
                        <GridItem colSpan={2} display="flex" justifyContent="center">
                            <div className="flex w-full justify-evenly items-center">
                                <button
                                    className={`bg-gray-300 text-black px-4 py-2 rounded font-medium transition-all duration-200 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-400 active:bg-gray-500"}`}
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                <Text fontSize="md" fontWeight="medium" color="gray.700" px={4}>
                                    Page {currentPage} of {totalPages}
                                </Text>
                                <button
                                    className={`bg-blue-500 text-white px-4 py-2 rounded font-medium transition-all duration-200 ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600 active:bg-blue-700"}`}
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </GridItem>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="">
            <div className="bg-mainBg p-3">
                <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
                    <div className="flex justify-between flex-wrap">
                        <div>
                            <h1 className="sm:text-4xl text-2xl font-semibold mb-4 text-left">
                                Unlocked Candidates
                            </h1>
                            <p className="opacity-80 mt-3.5 mb-4 text-black">View all candidate profiles you've unlocked</p>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <Link to="/employer-dashboard/search-candidate">
                                <button className="flex items-center px-2 py-1 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
                                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                    Find Candidates
                                </button>
                            </Link>
                        </div>
                    </div>

                    <div className="w-full bg-white rounded-xl">
                        <div className="flex flex-wrap justify-between items-center px-7 py-7 ">
                            <h2 className="text-lg font-semibold">My Unlocked Candidates</h2>
                            {candidates && candidates.data?.length > 0 && (
                                <button
                                    className="flex items-center px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 hover:border-green-300 transition-colors duration-200"
                                    onClick={handleExportToExcel}
                                    disabled={isExporting}
                                >
                                    <FontAwesomeIcon
                                        icon={faFileExcel}
                                        className={`mr-2 ${isExporting ? 'text-green-400' : 'text-green-600'}`}
                                    />
                                    {isExporting ? 'Exporting...' : 'Export to Excel'}
                                </button>
                            )}
                        </div>
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnlockedCandidate;